/**
 * Secure logging utility for production environments
 * Prevents sensitive data exposure in production logs
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogOptions {
  level?: LogLevel;
  skipInProduction?: boolean;
  sanitize?: boolean;
}

/**
 * Secure logger that respects environment settings
 */
class SecureLogger {
  private static isDev =
    typeof process !== "undefined"
      ? process.env.NODE_ENV === "development"
      : true;
  private static isDebugEnabled =
    typeof process !== "undefined"
      ? process.env.DEBUG_ENABLED === "true"
      : false;
  private static isVerboseEnabled =
    typeof process !== "undefined"
      ? process.env.VERBOSE_LOGS === "true"
      : false;
  private static isClient = typeof window !== "undefined";

  /**
   * Log a message with security considerations
   */
  static log(message: string, data?: any, options: LogOptions = {}) {
    const {
      level = "info",
      skipInProduction = true,
      sanitize = true,
    } = options;

    // Skip in production unless explicitly enabled
    if (
      !SecureLogger.isDev &&
      skipInProduction &&
      !SecureLogger.isVerboseEnabled
    ) {
      return;
    }

    // Skip debug logs unless debug is enabled
    if (level === "debug" && !SecureLogger.isDebugEnabled) {
      return;
    }

    const prefix = SecureLogger.isDev ? "[DEV]" : "[PROD]";
    const timestamp = new Date().toISOString();

    if (data) {
      const sanitizedData = sanitize ? SecureLogger.sanitizeData(data) : data;

      // eslint-disable-next-line no-console
      console.log(`${prefix} ${timestamp} - ${message}`, sanitizedData);
    } else {
      // eslint-disable-next-line no-console
      console.log(`${prefix} ${timestamp} - ${message}`);
    }
  }

  /**
   * Debug logging - only enabled in development with DEBUG_ENABLED=true
   * Safe for client-side usage
   */
  static debug(message: string, data?: any) {
    // Skip debug logs on client-side in production
    if (SecureLogger.isClient && !SecureLogger.isDev) {
      return;
    }

    SecureLogger.log(message, data, { level: "debug", skipInProduction: true });
  }

  /**
   * Info logging - visible in development and with VERBOSE_LOGS=true
   */
  static info(message: string, data?: any) {
    SecureLogger.log(message, data, { level: "info", skipInProduction: true });
  }

  /**
   * Production-safe logging - always visible but data is sanitized
   */
  static production(message: string, data?: any) {
    SecureLogger.log(message, data, {
      level: "info",
      skipInProduction: false,
      sanitize: true,
    });
  }

  /**
   * Error logging - always visible
   */
  static error(message: string, error?: any) {
    const prefix = SecureLogger.isDev ? "[DEV]" : "[PROD]";
    const timestamp = new Date().toISOString();

    // eslint-disable-next-line no-console
    console.error(`${prefix} ${timestamp} - ERROR: ${message}`, error);
  }

  /**
   * Sanitize sensitive data before logging
   */
  private static sanitizeData(data: any): any {
    if (!data) return data;

    // Handle arrays
    if (Array.isArray(data)) {
      return data.map((item) => SecureLogger.sanitizeData(item));
    }

    // Handle objects
    if (typeof data === "object" && data !== null) {
      const sanitized: any = {};

      for (const [key, value] of Object.entries(data)) {
        if (SecureLogger.isSensitiveField(key)) {
          sanitized[key] = SecureLogger.maskSensitiveValue(value);
        } else if (typeof value === "object") {
          sanitized[key] = SecureLogger.sanitizeData(value);
        } else {
          sanitized[key] = value;
        }
      }

      return sanitized;
    }

    return data;
  }

  /**
   * Check if a field contains sensitive information
   */
  private static isSensitiveField(fieldName: string): boolean {
    const sensitiveFields = [
      "ip",
      "clientip",
      "email",
      "password",
      "token",
      "key",
      "secret",
      "prompt",
      "response",
      "airesponse",
      "content",
      "message",
      "answers",
      "customrequest",
      "userid",
      "sessionid",
      "modificationhistory",
    ];

    return sensitiveFields.some((field) =>
      fieldName.toLowerCase().includes(field.toLowerCase()),
    );
  }

  /**
   * Mask sensitive values for logging
   */
  private static maskSensitiveValue(value: any): string {
    if (typeof value === "string") {
      if (value.includes("@")) {
        // Email masking
        const [local, domain] = value.split("@");

        return `${local.substring(0, 2)}***@${domain}`;
      }
      if (value.length > 20) {
        // Long strings (likely content/prompts)
        return `[CONTENT:${value.length}chars]`;
      }
      if (value.length > 5) {
        // Medium strings
        return `${value.substring(0, 3)}***`;
      }
    }

    if (typeof value === "object") {
      return `[OBJECT:${Object.keys(value).length}keys]`;
    }

    return "[MASKED]";
  }
}

/**
 * Convenience exports for common logging patterns
 * Bound methods to avoid context issues in client-side usage
 */
export const logger = {
  debug: (message: string, data?: any) => SecureLogger.debug(message, data),
  info: (message: string, data?: any) => SecureLogger.info(message, data),
  production: (message: string, data?: any) =>
    SecureLogger.production(message, data),
  error: (message: string, error?: any) => SecureLogger.error(message, error),
};
